import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { BillStatus } from "@boo/shared";
import { Button } from "@/components/ui/button";
import ConfirmModal from "@/components/ConfirmModal";
import { useBillStore } from "./store/useBillStore";
import { billTotal, sharesFor } from "./lib/billMath";
import { INPUT_CLASS } from "./types/bills.types";
import { useFriendsStore } from "@/features/friends/store/useFriendsStore";
import PeopleSection from "./components/PeopleSection";
import ItemRow from "./components/ItemRow";
import ChargesSection from "./components/ChargesSection";
import SharesFooter from "./components/SharesFooter";
import ReceiptScanButton from "./components/ReceiptScanButton";
import SharePanel from "./components/SharePanel";
import SlipReview from "./components/SlipReview";
import UnpaidFriends from "./components/UnpaidFriends";
import ReviewStatus from "./components/ReviewStatus";
import LiveDot from "./components/LiveDot";
import { useBillEventStream } from "./lib/eventStream";
import { PUBLIC_API_BASE } from "@/services/publicBillApi";

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error ? err.message : fallback;

const isTextField = (el: EventTarget) =>
  el instanceof HTMLElement &&
  (el.tagName === "TEXTAREA" ||
    (el.tagName === "INPUT" &&
      !["checkbox", "radio", "button", "submit"].includes(
        (el as HTMLInputElement).type,
      )));

export default function BillEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [pendingScan, setPendingScan] = useState<File | null>(null);
  const [typing, setTyping] = useState(false);
  const isNew = id === "new";

  const editor = useBillStore((s) => s.editor);
  const editorLoading = useBillStore((s) => s.editorLoading);
  const scanning = useBillStore((s) => s.scanning);
  const saving = useBillStore((s) => s.saving);
  const remoteChanged = useBillStore((s) => s.remoteChanged);
  const store = useBillStore.getState;
  const loadFriends = useFriendsStore((s) => s.loadFriends);

  // Avatars on the bill come from the Friend list.
  useEffect(() => {
    void loadFriends();
  }, [loadFriends]);

  useEffect(() => {
    const { editor: current, openBill, startNew } = store();
    if (isNew) {
      // Entry points call startNew first; a direct visit gets a blank draft.
      if (!current || current.billId !== null) startNew({});
    } else if (current?.billId !== id) {
      void (async () => {
        try {
          await openBill(id!);
        } catch (err) {
          toast.error(errorMessage(err, "Could not load the bill"));
          navigate("/bills", { replace: true });
        }
      })();
    }
  }, [id, isNew, navigate, store]);

  // Live updates: friends' claims / flags / slips and edits from other devices.
  const billId = editor?.billId ?? null;
  const live = useBillEventStream(
    billId ? `${PUBLIC_API_BASE}/bills/${billId}/events` : null,
    {
      auth: true,
      onRevision: async (revision) => {
        try {
          await store().syncRemote(revision);
        } catch {
          // next revision event retries
        }
      },
    },
  );

  const shares = useMemo(
    () =>
      editor
        ? sharesFor(editor.people, editor.items, editor.charges, editor.goDutch)
        : null,
    [editor],
  );

  if (!editor || !shares) {
    return (
      <>
        {editorLoading && (
          <SkeletonRegion className="space-y-4">
            <Skeleton className="h-8 w-1/2" />
            <ListRowsSkeleton rows={4} variant="cards" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-24 rounded-full" />
              <Skeleton className="h-9 w-24 rounded-full" />
              <Skeleton className="h-9 w-24 rounded-full" />
            </div>
          </SkeletonRegion>
        )}
      </>
    );
  }

  const readOnly = editor.status === BillStatus.Finalized;
  const total = billTotal(editor.items, editor.charges);
  const actions = store();

  async function run(action: () => Promise<unknown>, okMessage: string) {
    try {
      await action();
      toast.success(okMessage);
    } catch (err) {
      toast.error(errorMessage(err, "Something went wrong"));
    }
  }

  async function handleSave() {
    await run(async () => {
      const wasNew = editor!.billId === null;
      const savedId = await actions.save();
      if (wasNew) navigate(`/bills/${savedId}`, { replace: true });
    }, "Bill saved");
  }

  async function handleFinalize() {
    await run(async () => {
      const wasNew = editor!.billId === null;
      await actions.finalize();
      if (wasNew)
        navigate(`/bills/${store().editor!.billId}`, { replace: true });
    }, "Bill finalised — split saved to the expense");
  }

  async function handleScan(file: File) {
    if (editor!.items.length > 0) {
      setPendingScan(file);
      return;
    }
    await scan(file);
  }

  async function scan(file: File) {
    try {
      await actions.scanReceipt(file);
    } catch (err) {
      toast.error(errorMessage(err, "Could not read the receipt"));
    }
  }

  async function handleDelete() {
    setConfirmingDelete(false);
    if (editor!.billId) {
      await run(() => actions.removeBill(editor!.billId!), "Bill deleted");
    }
    actions.closeEditor();
    navigate("/bills", { replace: true });
  }

  return (
    <div
      onFocus={(e) => setTyping(isTextField(e.target))}
      onBlur={() => setTyping(false)}
    >
      <div className="pb-24">
        <div className="mb-4 flex items-center justify-between gap-3">
          <Link
            to="/bills"
            className="-ml-2 inline-flex min-h-11 items-center gap-1.5 px-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> Bills
          </Link>
          {editor.billId && <LiveDot live={live} />}
        </div>

        {editor.dirty && remoteChanged && (
          <div className="mb-4 flex flex-col gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <span>
              Someone updated this bill. Saving now will overwrite their
              changes.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  await actions.reloadRemote();
                } catch (err) {
                  toast.error(errorMessage(err, "Could not reload"));
                }
              }}
            >
              Reload
            </Button>
          </div>
        )}

        <div className="space-y-6">
          {editor.billId && !editor.dirty && <SlipReview editor={editor} />}
          {editor.billId && !editor.dirty && <UnpaidFriends editor={editor} />}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            <input
              aria-label="Bill title"
              value={editor.title}
              disabled={readOnly}
              placeholder="Where did you eat?"
              onChange={(e) => actions.setTitle(e.target.value)}
              className={`${INPUT_CLASS} min-w-0 text-base font-semibold`}
            />
            <input
              aria-label="Bill date"
              type="date"
              value={editor.date}
              disabled={readOnly}
              onChange={(e) => actions.setDate(e.target.value)}
              className={`${INPUT_CLASS} block w-full min-w-0 max-w-full appearance-none text-left sm:w-40`}
            />
          </div>

          <PeopleSection
            people={editor.people}
            readOnly={readOnly}
            onAddFriend={actions.addFriend}
            onAddGroup={actions.addGroup}
            onRelink={actions.relinkPerson}
            onRemove={actions.removePerson}
          />

          <section className="space-y-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">Items</h2>
              {!readOnly && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant={editor.goDutch ? "default" : "outline"}
                    size="sm"
                    aria-pressed={editor.goDutch}
                    disabled={editor.people.length < 2}
                    onClick={actions.toggleGoDutch}
                  >
                    <Users className="w-4 h-4 mr-1.5" /> หารเท่า
                  </Button>
                  <ReceiptScanButton
                    scanning={scanning}
                    label={editor.items.length ? "Rescan" : "Scan receipt"}
                    onFile={handleScan}
                  />
                </div>
              )}
            </div>
            <ul className="space-y-2">
              {editor.items.map((item) => (
                <ItemRow
                  key={item.key}
                  item={item}
                  people={editor.people}
                  readOnly={readOnly}
                  goDutch={editor.goDutch}
                  onChange={(patch) => actions.updateItem(item.key, patch)}
                  onRemove={() => actions.removeItem(item.key)}
                  onToggle={(pid) => actions.toggleClaim(item.key, pid)}
                  onClaimAll={() => actions.claimAll(item.key)}
                  onSplitUnits={() => actions.splitUnits(item.key)}
                />
              ))}
            </ul>
            {editor.items.length === 0 && !scanning && (
              <p className="text-sm text-muted-foreground">
                Scan the receipt or add items by hand.
              </p>
            )}
            {!readOnly && (
              <Button variant="outline" size="sm" onClick={actions.addItem}>
                <Plus className="w-4 h-4 mr-1.5" /> Add item
              </Button>
            )}
          </section>

          <ChargesSection
            charges={editor.charges}
            readOnly={readOnly}
            onAdd={actions.addCharge}
            onChange={actions.updateCharge}
            onRemove={actions.removeCharge}
          />

          {editor.billId && !editor.dirty && <ReviewStatus editor={editor} />}
          {editor.billId && !editor.dirty && <SharePanel editor={editor} />}
          {editor.billId && editor.dirty && (
            <p className="text-xs text-muted-foreground">
              Save your changes to share this bill.
            </p>
          )}

          <Button
            type="button"
            variant="destructive"
            onClick={() => setConfirmingDelete(true)}
            className="w-full sm:w-auto"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            {editor.billId ? "Delete bill" : "Discard draft"}
          </Button>
        </div>
      </div>

      <ConfirmModal
        open={confirmingDelete}
        danger
        title={editor.billId ? "Delete this bill?" : "Discard this draft?"}
        message={
          editor.billId
            ? "The bill and its split will be removed. The expense stays."
            : "Everything you entered on this draft will be lost."
        }
        confirmLabel={editor.billId ? "Delete bill" : "Discard draft"}
        cancelLabel="Keep"
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(false)}
      />

      <ConfirmModal
        open={pendingScan !== null}
        title="Replace current items?"
        message="The items on this bill will be replaced by the scanned receipt."
        confirmLabel="Replace"
        onConfirm={() => {
          const file = pendingScan!;
          setPendingScan(null);
          void scan(file);
        }}
        onCancel={() => setPendingScan(null)}
      />

      <SharesFooter
        editor={editor}
        shares={shares}
        total={total}
        saving={saving}
        hidden={typing}
        onSave={handleSave}
        onFinalize={handleFinalize}
        onReopen={() => run(actions.reopen, "Bill reopened")}
      />
    </div>
  );
}
